import {
  BookOpen,
  Building,
  Clock,
  FileText,
  Globe,
  Layers,
  RefreshCw,
  Shield,
  Users,
} from "lucide-react";
import { GoDotFill } from "react-icons/go";

const Features = () => {
  const features = [
    {
      id: 1,
      title: "Dual Mode Examination",
      description:
        "Conduct exams in both online and offline modes with seamless synchronization and security features.",
      icon: Layers,
      details: [
        "Online & Offline mode of conducting exam",
        "Secured and encrypted question download and answer upload",
        "Tamperproof examination process",
      ],
    },
    {
      id: 2,
      title: "Secure & Tamperproof",
      description:
        "Ensure the integrity of your examinations with our advanced security features.",
      icon: Shield,
      details: [
        "Encrypted question papers and answer sheets",
        "Secure browser environment to prevent cheating",
        "Audit trails for all actions during exams",
      ],
    },
    {
      id: 3,
      title: "Flexible Exam Management",
      description:
        "Configure exams with granular control over format, timing, and content.",
      icon: BookOpen,
      details: [
        "Multiple papers under an exam",
        "Complexity/marks based Question Bank setup",
        "Chapter/section wise question set",
      ],
    },
    {
      id: 4,
      title: "Advanced Question Settings",
      description:
        "Implement fair assessment with sophisticated question management capabilities.",
      icon: RefreshCw,
      details: [
        "Selective negative marking",
        "Randomization of question set per slot",
        "Randomization of answer keys in a slot",
      ],
    },
    {
      id: 5,
      title: "Exam Resume Capabilities",
      description:
        "Handle interruptions gracefully with our robust exam continuation features.",
      icon: Clock,
      details: [
        "Option for Mark for Review during exam",
        "Feature for Exam Resume with or w/o grace period",
        "Approval workflow and audit trail",
      ],
    },
    {
      id: 6,
      title: "Multilingual Support",
      description:
        "Deliver exams in multiple languages to accommodate diverse candidate needs.",
      icon: Globe,
      details: [
        "Support for multiple languages in the same exam",
        "Language switching during examination",
        "RTL language support",
      ],
    },
    {
      id: 7,
      title: "Result Processing",
      description:
        "Streamline post-exam workflows with our comprehensive result management system.",
      icon: FileText,
      details: [
        "Bulk result processing",
        "Online result publishing",
        "Automated notification to examinee",
      ],
    },
    {
      id: 8,
      title: "Scrutiny Workflow",
      description:
        "Ensure fairness with our structured review and objection handling system.",
      icon: Users,
      details: [
        "Scrutiny workflow with option for raising objection",
        "Objection against an answer",
        "Publish result post scrutiny",
      ],
    },
    {
      id: 9,
      title: "Test Centre Management",
      description:
        "Efficiently manage examination logistics with our test center administration tools.",
      icon: Building,
      details: [
        "Test Centre management - Online & Offline Test Centres",
        "Candidate to slot mapping",
        "Email/SMS notification to candidates",
      ],
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-sky-50 to-indigo-50 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-blue-400 blur-xl"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full bg-indigo-400 blur-xl"></div>
        <div className="absolute top-1/2 right-5 w-24 h-24 rounded-full bg-blue-300 blur-lg"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 animate-gradient mb-4">
            Powerful Features
          </h2>
          <div className="flex justify-center mb-2">
            <span className="inline-block w-28 h-[5px] bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full shadow-md "></span>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to conduct secure, scalable, and efficient
            examinations
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-[#eef9ff] p-6 rounded-xl shadow-inner border border-[#d5ebf7] hover:shadow-md transition-all duration-300 cursor-pointer z-50"
            >
              <div className="flex items-center mb-2">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 group-hover:from-blue-50 group-hover:to-indigo-50 flex items-center justify-center transition-all duration-500">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md group-hover:scale-110 transition-transform duration-500">
                    <feature.icon className="h-5 w-5" />
                  </div>
                </div>

                <h3 className="text-base font-semibold text-blue-800 ml-2">
                  {feature.title}
                </h3>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed text-justify">
                {feature.description}
              </p>
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">
                  Key Capabilities:
                </h4>
                <ul className="space-y-2">
                  {feature.details.map((detail, index) => (
                    <li key={index} className="flex items-start">
                      <GoDotFill className="text-blue-500 mt-0.5 mr-2 shrink-0" />
                      <span className="text-sm text-gray-700 leading-snug">
                        {detail}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
